import { Link } from "react-router-dom";
import { funnelHref } from "@/lib/site";

const CAREER = funnelHref("/?diagnostic=1");
const HERO = "/aor-assets/hero.webp";

export const scrollToLab = () => document.getElementById("lab")?.scrollIntoView({ behavior: "smooth", block: "start" });

export const SiteNav = () => (
  <nav className="nav shell">
    <a className="brand" href="#top" data-testid="site-brand"><span className="brandWords">Art of Resume<small>HR will get back ... this time</small></span></a>
    <div className="navlinks">
      <Link to="/resume-service" data-testid="site-nav-studio">Resume Studio</Link>
      <a href={CAREER} data-testid="site-nav-career">AI Career Discovery</a>
      <Link to="/blog" data-testid="site-nav-blog">Blog</Link>
      <a href="#method" data-testid="site-nav-resources">Resources</a>
      <a href="#founder" data-testid="site-nav-about">About</a>
      <button className="navCta heroAnalyze" type="button" onClick={scrollToLab} data-testid="site-nav-cta">Start My Free Resume Diagnostic →</button>
    </div>
  </nav>
);

const Trust = ({ t, s }) => <span><b>✓</b><span><strong>{t}</strong>{s}</span></span>;

export const SiteHero = ({ onUpload }) => (
  <section className="hero shell heroV2" id="top">
    <div className="heroCopy">
      <p className="eyebrow heroPill">RESUME STUDIO · EVIDENCE FIRST. NOT JUST KEYWORDS.</p>
      <h1>Your Resume Has a Story.<br /><em>Let's Show Its True Value.</em></h1>
      <p className="lead">Get a free, privacy-first analysis of your resume in seconds. See what's working, what's missing, and how to better position your experience for today's opportunities.</p>
      <div className="heroActions">
        <button className="primary heroAnalyze heroUpload" type="button" onClick={onUpload} data-testid="site-hero-upload-btn">
          <span className="uploadGlyph" aria-hidden="true">↑</span> Upload My Resume Now <span aria-hidden="true">→</span>
        </button>
      </div>
      <p className="heroMicro">Supports PDF, DOCX, or pasted text <i /> Takes less than 2 minutes</p>
      <div className="trust">
        <Trust t="Privacy-first" s="Your data stays yours." />
        <Trust t="Evidence before advice" s="See why each fix matters." />
        <Trust t="No fake AI claims" s="Nothing you cannot defend." />
        <Trust t="Built for experience" s="Not only entry-level resumes." />
      </div>
    </div>
    <div className="heroVisual">
      <img src={HERO} alt="Experienced professional reviewing a resume at a desk" loading="eager" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
      <div className="visualShade" />
      <div className="heroNote heroNotePrimary"><b>Same experience.</b><span>Bigger opportunities.</span></div>
      <blockquote className="doorMessage"><span>Good resumes<br />open doors.</span></blockquote>
      <a className="heroPlay" href="#method" aria-label="See how ArtOfResume works" data-testid="site-hero-play"><span aria-hidden="true">▶</span><small>See how it works</small></a>
      <div className="heroQuote">“It's not about adding more. It's about showing what you've already done—more clearly.”<small>— ArtOfResume method</small></div>
      <div className="heroProof"><b>10</b><span>evidence signals<br />checked instantly</span></div>
    </div>
  </section>
);

export const PathBand = () => (
  <section className="pathBand">
    <div className="shell pathGrid">
      <div className="pathIntro"><p className="eyebrow">CHOOSE YOUR NEXT STEP</p><h2>Start with the outcome you need.</h2></div>
      <a href="#lab" data-testid="site-path-analyze"><span>01</span><b>Analyze my resume</b><small>Free, instant, browser-only diagnostic</small></a>
      <Link to="/resume-service" data-testid="site-path-develop"><span>02</span><b>Develop my resume</b><small>AI-assisted rewrite with PDF and DOCX</small></Link>
      <a href={CAREER} data-testid="site-path-career"><span>03</span><b>Discover my AI career path</b><small>A 5-minute diagnostic and a personalised PDF guide</small></a>
    </div>
  </section>
);

const RevealItem = ({ i, t, s }) => <div><span>{i}</span><p><b>{t}</b><small>{s}</small></p></div>;
const Line = ({ l, v, c }) => <div className="scoreLine"><span>{l}</span><div><i className={c} style={{ width: `${v}%` }} /></div><b>{v}/100</b></div>;
const Bars = ({ n }) => Array.from({ length: n }, (_, k) => <i key={k} />);

export const RevealSection = () => (
  <section className="revealSection shell">
    <div className="revealHeading"><h2>See What Your Free Resume Diagnostic Reveals</h2><p>In seconds, get a clear picture of where you stand and what to improve.</p></div>
    <div className="revealGrid">
      <article className="revealList">
        <RevealItem i="▥" t="Resume clarity score" s="How easily your professional value can be understood." />
        <RevealItem i="!" t="Missing evidence" s="What is not showing—but should be." />
        <RevealItem i="⌕" t="Skills and keywords" s="How well your resume supports target roles." />
        <RevealItem i="▤" t="Structure and ATS readiness" s="Format, section flow, and machine readability." />
        <RevealItem i="✓" t="Actionable recommendations" s="Specific, practical next steps based on the document." />
      </article>
      <article className="scorePreview">
        <div className="previewScore"><strong>72</strong><span>/100</span><small>Good foundation<br />Room to grow</small></div>
        <Line l="Relevance to target role" v={80} c="green" />
        <Line l="Impact and achievements" v={65} c="orange" />
        <Line l="Skills and keywords" v={70} c="blue" />
        <Line l="Structure and clarity" v={75} c="violet" />
        <Line l="ATS friendliness" v={60} c="red" />
        <p>Illustrative preview. Your score is calculated from your own document.</p>
      </article>
      <article className="transformPreview">
        <div className="docCompare">
          <div className="transformStage"><h3>From This...</h3>
            <div className="miniDoc before"><span>BEFORE</span><b>DEMO CANDIDATE</b><small>Experienced technology professional</small><i /><i /><i className="short" /><h4>Experience</h4><i /><i /><i className="short" /><h4>Skills</h4><i /><i /></div>
          </div>
          <div className="compareArrow">→</div>
          <div className="transformStage"><h3>...To This</h3>
            <div className="miniDoc after"><span>AFTER</span><b>DEMO CANDIDATE</b><small>QA Automation Lead · Quality Engineering</small><h4>Professional value</h4><Bars n={3} /><h4>Evidence-led achievements</h4><Bars n={3} /><h4>Core expertise</h4><Bars n={2} /></div>
          </div>
        </div>
        <div className="valueOutcome">
          <ul><li>Clearer value proposition</li><li>Stronger evidence hierarchy</li><li>Relevant, supported keywords</li><li>Cleaner ATS-friendly structure</li></ul>
          <p>Same experience.<br /><strong>A stronger story.</strong></p>
        </div>
      </article>
    </div>
  </section>
);

export const EarlyCta = () => (
  <section className="finalCta earlyCta">
    <div className="shell finalCtaInner">
      <div><p className="eyebrow">YOUR FREE 2-MINUTE CHECK</p><h2>See what your resume is not showing yet.</h2>
        <p>Upload the document you already have. Get a clarity score, evidence gaps, and prioritized fixes before deciding what to do next.</p></div>
      <button className="heroAnalyze" type="button" onClick={scrollToLab} data-testid="site-early-cta">Analyze my resume →</button>
      <div className="finalTrust"><span>✓ Free diagnostic</span><span>✓ No login required</span><span>✓ No credit card</span><span>✓ Browser-only analysis</span></div>
    </div>
  </section>
);

const Tile = ({ i, t, s }) => <div><i>{i}</i><span><b>{t}</b>{s}</span></div>;

export const ProblemSolution = () => (
  <section className="problemSolution">
    <div className="shell problemGrid">
      <article className="problemSide"><p className="eyebrow redText">THE PROBLEM</p><h2>You've done meaningful work.<br />But most resumes don't show it.</h2>
        <p>A resume can list responsibilities without proving the impact you made. Recruiters scan quickly, so important evidence is easily missed when scope, outcomes, and decisions are buried.</p></article>
      <article className="solutionSide"><p className="eyebrow greenText">THE SOLUTION</p><h2>Resume Studio: An Evidence-First Approach</h2>
        <p>We turn observable facts into clearer positioning—without inventing achievements or overstating your experience.</p>
        <div className="solutionTiles">
          <Tile i="⌕" t="Evidence analysis" s="Find the proof already in your experience." />
          <Tile i="♟" t="Human-ready review" s="Make every important point easy to scan." />
          <Tile i="✓" t="Strategic positioning" s="Align verified strengths to target roles." />
          <Tile i="↗" t="Practical next steps" s="Know exactly what to improve first." />
        </div>
      </article>
    </div>
  </section>
);
